import {
  collection,
  addDoc,
  doc,
  updateDoc,
  increment,
  serverTimestamp,
  Timestamp,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { AnalyticsEvent, DashboardMetrics } from '@/types';
import { AuthService } from './auth';

export class AnalyticsService {
  private static collection = 'analytics_events';
  private static propertiesCollection = 'properties';
  private static matchesCollection = 'matches';
  private static clientsCollection = 'clients';

  // Helper to get or create a session ID
  private static getSessionId(): string {
    if (typeof sessionStorage === 'undefined') {
      return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    const STORAGE_KEY = 'analytics_session_id';
    let sessionId = sessionStorage.getItem(STORAGE_KEY);
    
    if (!sessionId) {
      sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(STORAGE_KEY, sessionId);
    }
    
    return sessionId;
  }

  /**
   * Track a generic analytics event
   * @param eventType The type of event (e.g., 'view_property', 'login', 'search')
   * @param eventData Additional data associated with the event
   * @param userId Optional user ID (defaults to current authenticated user or 'anonymous')
   */
  static async trackEvent(
    eventType: string,
    eventData: { [key: string]: any } = {},
    userId?: string
  ): Promise<string> {
    try {
      // Get current user if userId is not provided
      let finalUserId = userId;
      if (!finalUserId) {
        const currentUser = await AuthService.getCurrentUser();
        finalUserId = currentUser?.id || 'anonymous';
      }

      // Get environment info
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
      const referrer = typeof document !== 'undefined' ? document.referrer : undefined;

      // Create event object
      const event: Omit<AnalyticsEvent, 'id'> = {
        userId: finalUserId,
        eventType,
        eventData,
        timestamp: Timestamp.now(),
        sessionId: this.getSessionId(),
        userAgent,
        ipAddress: 'unknown', // Client-side can't reliably get this
        referrer,
      };

      const docRef = await addDoc(collection(db, this.collection), event);
      return docRef.id;
    } catch (error: any) {
      console.error('Analytics Error:', error);
      return '';
    }
  }

  /**
   * Helper to track page views
   */
  static async trackPageView(pageName: string, path: string): Promise<string> {
    return this.trackEvent('page_view', {
      pageName,
      path,
    });
  }

  /**
   * Helper to track property views
   * Logs the event and increments the property view counter
   */
  static async trackPropertyView(propertyId: string): Promise<string> {
    try {
      const eventId = await this.trackEvent('property_view', {
        propertyId,
      });

      // Increment property view counter
      // We do this in parallel/fire-and-forget to not block
      const propertyRef = doc(db, this.propertiesCollection, propertyId);
      updateDoc(propertyRef, {
        'analytics.views': increment(1),
      }).catch((err) => {
        console.error('Failed to increment property views:', err);
      });

      return eventId;
    } catch (error: any) {
      console.error('Analytics Error:', error);
      return '';
    }
  }

  /**
   * Helper to track match interactions
   * Logs when a user interacts with a match (views, accepts, rejects, etc.)
   */
  static async trackMatchInteraction(
    matchId: string,
    interactionType: string,
    additionalData: { [key: string]: any } = {}
  ): Promise<string> {
    try {
      const eventId = await this.trackEvent('match_interaction', {
        matchId,
        interactionType,
        ...additionalData,
      });

      // Update match analytics
      const matchRef = doc(db, this.matchesCollection, matchId);
      const updateData: any = {
        'analytics.updatedAt': serverTimestamp(),
      };

      if (interactionType === 'viewed') {
        updateData['analytics.viewedAt'] = serverTimestamp();
      } else if (interactionType === 'interested' || interactionType === 'accepted') {
        updateData['analytics.respondedAt'] = serverTimestamp();
      }

      updateDoc(matchRef, updateData).catch((err) => {
        console.error('Failed to update match analytics:', err);
      });

      return eventId;
    } catch (error: any) {
      console.error('Analytics Error:', error);
      return '';
    }
  }

  /**
   * Get property analytics
   * Aggregates property view counts, engagement metrics, and match statistics
   */
  static async getPropertyAnalytics(propertyId: string): Promise<{
    views: number;
    favorites: number;
    inquiries: number;
    shares: number;
    avgTimeOnPage: number;
    conversionRate: number;
    matchCount: number;
    avgMatchScore: number;
  }> {
    try {
      // Get property document
      const propertyRef = doc(db, this.propertiesCollection, propertyId);
      const propertySnap = await getDoc(propertyRef);

      if (!propertySnap.exists()) {
        throw new Error('Property not found');
      }

      const property = propertySnap.data();
      const analytics = property.analytics || {};

      // Get match statistics
      const matchesQuery = query(
        collection(db, this.matchesCollection),
        where('propertyId', '==', propertyId)
      );
      const matchesSnapshot = await getDocs(matchesQuery);
      const matches = matchesSnapshot.docs.map(doc => doc.data());
      
      const matchCount = matches.length;
      const avgMatchScore = matches.length > 0
        ? matches.reduce((sum, match) => sum + (match.score?.overall || 0), 0) / matches.length
        : 0;

      return {
        views: analytics.views || 0,
        favorites: analytics.favorites || 0,
        inquiries: analytics.inquiries || 0,
        shares: analytics.shares || 0,
        avgTimeOnPage: analytics.avgTimeOnPage || 0,
        conversionRate: analytics.conversionRate || 0,
        matchCount,
        avgMatchScore: Math.round(avgMatchScore),
      };
    } catch (error: any) {
      throw new Error(`Failed to get property analytics: ${error.message || error}`);
    }
  }

  /**
   * Get client analytics
   * Aggregates client interaction counts, match statistics, and engagement metrics
   */
  static async getClientAnalytics(clientId: string): Promise<{
    interactionCount: number;
    matchCount: number;
    avgMatchScore: number;
    interestedMatches: number;
    scheduledViewings: number;
    engagementScore: number;
  }> {
    try {
      // Get client document
      const clientRef = doc(db, this.clientsCollection, clientId);
      const clientSnap = await getDoc(clientRef);

      if (!clientSnap.exists()) {
        throw new Error('Client not found');
      }

      const client = clientSnap.data();
      const interactions = client.interactions || [];

      // Get match statistics
      const matchesQuery = query(
        collection(db, this.matchesCollection),
        where('clientId', '==', clientId)
      );
      const matchesSnapshot = await getDocs(matchesQuery);
      const matches = matchesSnapshot.docs.map(doc => doc.data());
      
      const matchCount = matches.length;
      const avgMatchScore = matches.length > 0
        ? matches.reduce((sum, match) => sum + (match.score?.overall || 0), 0) / matches.length
        : 0;
      
      const interestedMatches = matches.filter(m => m.status === 'interested').length;
      const scheduledViewings = matches.filter(m => m.status === 'scheduled').length;

      // Calculate engagement score (simplified)
      const engagementScore = Math.min(100, 
        (interactions.length * 10) + 
        (matchCount * 5) + 
        (interestedMatches * 15) + 
        (scheduledViewings * 20)
      );

      return {
        interactionCount: interactions.length,
        matchCount,
        avgMatchScore: Math.round(avgMatchScore),
        interestedMatches,
        scheduledViewings,
        engagementScore: Math.round(engagementScore),
      };
    } catch (error: any) {
      throw new Error(`Failed to get client analytics: ${error.message || error}`);
    }
  }

  /**
   * Get dashboard metrics
   * Calculates total properties, clients, matches, success rates, and average scores
   */
  static async getDashboardMetrics(): Promise<DashboardMetrics> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to get dashboard metrics');
      }

      // Get total properties
      const allPropertiesQuery = query(collection(db, this.propertiesCollection));
      const allPropertiesSnapshot = await getDocs(allPropertiesQuery);
      const totalProperties = allPropertiesSnapshot.size;

      // Get active properties
      const activePropertiesQuery = query(
        collection(db, this.propertiesCollection),
        where('status', '==', 'active')
      );
      const activePropertiesSnapshot = await getDocs(activePropertiesQuery);
      const activeProperties = activePropertiesSnapshot.size;

      // Get total clients (scoped to agent if not admin)
      let allClientsQuery = query(collection(db, this.clientsCollection));
      if (currentUser.role === 'agent') {
        allClientsQuery = query(allClientsQuery, where('agentId', '==', currentUser.id));
      }
      const allClientsSnapshot = await getDocs(allClientsQuery);
      const totalClients = allClientsSnapshot.size;

      // Get active clients
      let activeClientsQuery = query(
        collection(db, this.clientsCollection),
        where('status', '==', 'active')
      );
      if (currentUser.role === 'agent') {
        activeClientsQuery = query(activeClientsQuery, where('agentId', '==', currentUser.id));
      }
      const activeClientsSnapshot = await getDocs(activeClientsQuery);
      const activeClients = activeClientsSnapshot.size;

      // Get total matches
      let matchesQuery = query(collection(db, this.matchesCollection));
      if (currentUser.role === 'agent') {
        matchesQuery = query(matchesQuery, where('agentId', '==', currentUser.id));
      }
      const matchesSnapshot = await getDocs(matchesQuery);
      const matches = matchesSnapshot.docs.map(doc => doc.data());
      const totalMatches = matches.length;

      // Calculate successful matches (interested + scheduled)
      const successfulMatches = matches.filter(
        m => m.status === 'interested' || m.status === 'scheduled'
      ).length;

      // Calculate conversion rate
      const conversionRate = totalMatches > 0
        ? (successfulMatches / totalMatches) * 100
        : 0;

      return {
        totalProperties,
        activeProperties,
        totalClients,
        activeClients,
        totalMatches,
        successfulMatches,
        conversionRate: Math.round(conversionRate * 100) / 100,
        revenue: 0, // TODO: Calculate from closed deals
        monthlyGrowth: 0, // TODO: Calculate growth percentage
        topPerformingProperties: [], // TODO: Get top properties by views/matches
        recentActivity: [], // TODO: Can be populated with recent events
      };
    } catch (error: any) {
      throw new Error(`Failed to get dashboard metrics: ${error.message || error}`);
    }
  }
}
